/**
 * Builds the OIDC provider settings for react-oidc-context from the loaded {@link RuntimeConfig}.
 *
 * Cognito is used as the OIDC provider: the `authority` is the User Pool issuer, from which
 * oidc-client-ts discovers the endpoints via `<authority>/.well-known/openid-configuration`. The
 * dashboard uses the authorization-code flow with PKCE (`response_type=code`), the `openid` scope,
 * and returns to the SPA origin (`redirect_uri`).
 *
 * NOTE (IaC follow-up, documented in README): for the login redirect to actually resolve, the
 * Cognito app client needs a Hosted-UI domain plus the SPA origin registered as an allowed
 * callback/logout URL. The OAuth code flow + `openid` scope are already enabled on the app client;
 * the Hosted-UI domain and real callback URLs are the remaining infrastructure work.
 */

import type { AuthProviderProps } from 'react-oidc-context';

import type { RuntimeConfig } from '../config';

/**
 * Clean the OAuth `?code=...&state=...` query parameters off the URL after a successful sign-in
 * redirect, so a refresh does not attempt to re-process a consumed authorization code.
 */
export function onSigninCallback(): void {
  if (typeof window !== 'undefined') {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

/** Translate the runtime config into react-oidc-context provider settings. */
export function buildOidcConfig(config: RuntimeConfig): AuthProviderProps {
  return {
    authority: config.cognitoAuthority,
    client_id: config.cognitoClientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: 'openid',
    // No client secret (public SPA client); oidc-client-ts uses PKCE for the code flow by default.
    onSigninCallback,
  };
}
